// Retention color — THE single client-side derivation, used by Home (board +
// queue), Clients (?color= filter + row dots) and ClientDetail (header dot).
// It mirrors v_retention.retention_color (migration 014) exactly:
//
//   green   cadence_ratio <= 1.25
//   yellow  cadence_ratio > 1.25 and <= 2.5
//   red     cadence_ratio > 2.5 OR (frequency = 1 AND recency_days > 45)
//   NULL cadence_ratio:
//     frequency 1   → recency_days <= 45 ? green : red
//     frequency >= 2 → recency_days <= 60 ? green : yellow
//
// Computed, never stored (SPEC §0). Accepts v_retention rows (frequency /
// recency_days) and v_client_list rows (visits / last_visit) interchangeably.

import type { RetentionColor } from "./types";

/** Column emoji per color (board headers, filter options). */
export const RETENTION_EMOJI: Record<RetentionColor, string> = {
  green: "🟢",
  yellow: "🟡",
  red: "🔴",
};

/** Short human labels per color. */
export const RETENTION_LABELS: Record<RetentionColor, string> = {
  green: "On rhythm",
  yellow: "Drifting",
  red: "At risk",
};

/** Tooltip copy explaining what each color means. */
export const RETENTION_TITLES: Record<RetentionColor, string> = {
  green: "On their usual visit rhythm (cadence ≤ 1.25×)",
  yellow: "Drifting past their usual rhythm (1.25–2.5×)",
  red: "Way past their rhythm (>2.5×), or one visit 45+ days ago",
};

/** All colors in board order. */
export const RETENTION_COLORS: RetentionColor[] = ["green", "yellow", "red"];

/** The subset of client fields the derivation needs; extra fields are fine. */
export interface RetentionColorInput {
  /** recency_days / median_gap_days — null with <2 visits. */
  cadence_ratio?: number | string | null;
  /** Visit count — v_retention naming. */
  frequency?: number | string | null;
  /** Visit count — v_client_list naming (fallback when frequency is absent). */
  visits?: number | string | null;
  /** Days since last visit — v_retention naming. */
  recency_days?: number | string | null;
  /** Last visit date — v_client_list fallback for recency_days. */
  last_visit?: string | null;
}

/** PostgREST can serialize numerics as strings; normalize before math. */
function num(v: number | string | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isNaN(n) ? null : n;
}

/** YYYY-MM-DD of a Date in the given zone (en-CA locale formats exactly that). */
function zonedYmd(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/**
 * Whole days between a visit date and today, both as NY business dates.
 * Date-only strings are never timezone-shifted (same rules as lib/dates.ts).
 */
function daysSinceNy(d: string): number | null {
  const s = d.trim();
  let thenYmd: string;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    thenYmd = s;
  } else {
    const dt = new Date(s);
    if (Number.isNaN(dt.getTime())) return null;
    thenYmd = zonedYmd(dt, "America/New_York");
  }
  const todayYmd = zonedYmd(new Date(), "America/New_York");
  const [ty, tm, td] = thenYmd.split("-").map(Number);
  const [ny, nm, nd] = todayYmd.split("-").map(Number);
  return Math.round((Date.UTC(ny, nm - 1, nd) - Date.UTC(ty, tm - 1, td)) / 86_400_000);
}

/**
 * Retention color for one client row; null when the client has no visits yet
 * (no rhythm to judge — they never enter the board or get a dot).
 */
export function retentionColor(row: RetentionColorInput): RetentionColor | null {
  const frequency = num(row.frequency) ?? num(row.visits);
  if (frequency == null || frequency < 1) return null;

  let recency = num(row.recency_days);
  if (recency == null && row.last_visit) recency = daysSinceNy(row.last_visit);

  // One-timers 45+ days out are red regardless of (necessarily null) cadence.
  if (frequency === 1 && recency != null && recency > 45) return "red";

  const ratio = num(row.cadence_ratio);
  if (ratio != null) {
    if (ratio > 2.5) return "red";
    if (ratio > 1.25) return "yellow";
    return "green";
  }

  // No rhythm yet (null cadence_ratio).
  if (frequency >= 2) return recency == null || recency <= 60 ? "green" : "yellow";
  return recency == null || recency <= 45 ? "green" : "red";
}
